import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { DishForm, DishFormValues, DishItem } from '~/components/DishForm';
import Heading from '~/components/Shared/Heading/Heading';
import Page from '~/components/Shared/Layout/Page/Page';
import styles from './EditDish.module.scss';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useFoodCurrency } from '~/utils/hooks/useFoodCurrency';
import { getPaginate } from '~/utils/getPaginate';
interface FoodItem {
  food_menu_id?: number;
  asset_id?: number;
}

interface FoodMenu {
  data: DishItem;
}

const EditDish = (): JSX.Element => {
  const { dishID } = useParams();
  const { foodID } = useParams();
  const navigate = useNavigate();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const { value, paginate } = getPaginate();
  const { value: foodValue, paginate: foodPaginate } = getPaginate('foodMenu');

  const client = useQueryClient();

  const { data: foodItem } = useQuery<FoodItem>({
    queryFn: async () => {
      const response = await instance.get(`food-admin/${foodID}`);

      return response.data;
    },
    queryKey: ['food-item', foodID],
    onError: error => {
      toastError(error);
    },
  });

  const foodMenuId = foodItem?.food_menu_id;

  const { data } = useQuery<FoodMenu>({
    queryFn: async () => {
      const response = await instance.get(`admin/ifcm-wares-all/${dishID}`);
      return response.data;
    },
    queryKey: ['dish', dishID],
    keepPreviousData: true,
    onError: error => {
      toastError(error);
    },
  });

  const dishItem = data?.data;
  const currency = useFoodCurrency(foodID);

  const { mutateAsync: updateDish } = useMutation(
    async (values: DishFormValues) => {
      if (!dishItem) return;

      const response = await instance.patch(
        `admin/ifcm-wares-all/${dishItem.id}`,
        {
          wares_name: values.wares_name,
          weight: values.weight,
          measure: values.measure,
          ingredients: values.ingredients,
          photo_ids: values.photos.map(p => p.id),
        }
      );

      return response.data;
    },
    {
      onSuccess: (data: DishFormValues) => {
        client.setQueryData(
          ['menu-dishes', foodMenuId],
          (oldDishes?: DishFormValues[]) => {
            if (!oldDishes) {
              return [data];
            } else {
              return [...oldDishes, data];
            }
          }
        );
        toastSuccess('Меню отредактировано');
        navigate(
          `/food/${foodID}?${value}=${paginate}&${foodValue}=${foodPaginate}`
        );
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  return (
    <Page
      heading={
        <Heading
          text={dishItem?.wares_name}
          classNameText={styles['heading-text']}
        />
      }
    >
      <DishForm
        dishItem={dishItem}
        assetId={foodItem?.asset_id}
        currency={currency}
        onSubmit={values => updateDish(values)}
      />
    </Page>
  );
};

export default EditDish;
